import React, { useState } from 'react';
import { SupportingDocument } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { toast } from 'react-toastify';

type SupportingDocumentsSectionProps = {
  orderId: number;
  documents: SupportingDocument[];
  onDocumentsChange: () => void;
};

const SupportingDocumentsSection: React.FC<SupportingDocumentsSectionProps> = ({
  orderId,
  documents,
  onDocumentsChange,
}) => {
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const getCategoryDisplayName = (category: string) => {
    return category.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const handleDownload = async (doc: SupportingDocument) => {
    try {
      const response = await fetchWithAuth(`/supporting_documents/${doc.id}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Failed to download document');
      }
    } catch (error) {
      toast.error('Error downloading document');
    }
  };

  const handleDelete = async (docId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeletingId(docId);
    try {
      const response = await fetchWithAuth(`/supporting_documents/${docId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Document deleted successfully');
        onDocumentsChange();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Error deleting document');
    } finally {
      setDeletingId(null);
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    const colors: { [key: string]: string } = {
      quote: 'bg-purple-200 text-purple-800',
      invoice: 'bg-blue-200 text-blue-800',
      proof_of_payment: 'bg-green-200 text-green-800',
      bank_statement: 'bg-yellow-200 text-yellow-800',
      other: 'bg-gray-200 text-gray-800',
    };
    return colors[category] || 'bg-gray-200 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (documents.length === 0) {
    return (
      <p className="text-gray-500 text-sm">No supporting documents uploaded yet</p>
    );
  }

  return (
    <div className="space-y-3">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="bg-white border rounded-md p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryBadgeColor(doc.category)}`}>
                {getCategoryDisplayName(doc.category)}
              </span>
              <span className="text-xs text-gray-500">
                {doc.file_size_mb} MB
              </span>
            </div>
            <p className="font-medium text-sm">{doc.original_filename}</p>
            <p className="text-xs text-gray-500 mt-1">
              Uploaded {formatDate(doc.created_at)}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleDownload(doc)}
              className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-1"
              title="Download document"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Download</span>
            </button>
            <button
              onClick={() => handleDelete(doc.id)}
              disabled={deletingId === doc.id}
              className={`px-3 py-2 rounded text-sm text-white flex items-center gap-1 ${
                deletingId === doc.id
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              title="Delete document"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Delete</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SupportingDocumentsSection;
